import { View, html } from 'rune-ts';
import { Input_TextEditor } from '@services/MarppleShopStudio/shared/component/atom/Input/Input_TextEditor.ts';
import {
  errorMessage,
  formKeyMap,
} from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/shared.ts';
import { createCreatorProductStore } from '@services/MarppleShopStudio/widget/CreatorProduct/core/store/createStore.ts';
import {
  createSubscribeStoreList,
  subscribeStore,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/section-views/helper.ts';
import { Input_RadioGroup } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_RadioGroup.ts';
import {
  CreatorProductDefaultData,
  MODE,
} from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/types.ts';
import { Input_Category } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Category.ts';
import { Input_Price } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Price.ts';
import { MONEY_SIGN } from '@services/MarppleShop/shared/constant';
import { MembershipSettingSection } from '@modules/Creator/Setting/S/MembershipSettingForm/MembershipSettingSection.ts';
import { isMembershipSupported } from '@modules/Util/S/Function/membership.ts';
import {
  MultiSelectButtonGroup,
  MultiSelectButtonGroupClickEvent,
} from '@services/MarppleShop/renderApp/components/cells/MultiSelectButtonGroup/MultiSelectButtonGroup.ts';
import { BaseSection } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/section-views/BaseSection.ts';
import { compact, pipe, toArray, isEmpty, each, isNumber, isNil, map, reduce } from '@fxts/core';
import { EditorInputFocusoutEvent } from '@services/MarppleShopStudio/shared/component/atom/Input/core/events.ts';
import { FORM_DROPDOWN_CONSTANT } from '@services/MarppleShop/shared/components/cells/FormDropDown/type.ts';
import klass from './Section.module.scss';
import { Input_TextEditor_Intl } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_TextEditor_Intl.ts';
import { CreatorProductState } from '@services/MarppleShopStudio/widget/CreatorProduct/core/store/types.ts';
import { assignKeys, deleteKeys } from '../../../../../../../shared/utils/object-utils.ts';
import { Input_AddionalRadioGroup } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_AddionalRadioGroup.ts';
import { UtilS } from '@modules/Util/S/Function/module/UtilS';
import { Input_NumberEditor } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_NumberEditor.ts';
import { PriceS } from '@modules/Price/S/Function/module/PriceS';
import {
  calcDiscountedRateByPercent,
  getPreDiscountRateDisplayValue,
} from '@modules/MShop/Studio/PreDiscountProduct/S/Function/preDiscountProduct';
import common_klass from './styles/common.module.scss';
import { Tooltip } from '@services/MarppleShop/renderApp/components/atoms/Tooltip/Tooltip.ts';
import { shareFont } from '@services/MarppleShop/shared/typography';
import { PreDiscountPriceExampleImage } from '@modules/MShop/Studio/PreDiscountProduct/S/Constant/preDiscountConst';
import { required_html } from '@services/MarppleShopStudio/shared/component/atom/Input/core/helper.ts';
import { CategoryLevel2_CODE } from '@shared/mps-shared/domain/category/types.ts';
import { MShopStudioPbProductTmplS } from '@modules/MShop/Studio/PbProduct/S/Tmpl/module/MShopStudioPbProductTmplS';
import format from 'date-fns/format/index.js';

interface MainInfoSectionData<T extends MODE> {
  store: ReturnType<typeof createCreatorProductStore<T>>;
  mpsCategories: CreatorProductDefaultData['mpsCategories'];
  products_memberships: CreatorProductDefaultData['products_memberships'];
  goods_type_id: CreatorProductDefaultData['goods_type_id'];
  is_created_sku?: boolean;
  is_plus?: boolean;
  is_public?: boolean;
  is_studio_worker_admin?: boolean;
  is_crew_admin?: boolean;
}

export class MainInfoSection<T extends MODE> extends BaseSection<T, MainInfoSectionData<T>> {
  private subscribeStoreList = createSubscribeStoreList();
  private store = this.data.store;
  protected readonly sectionTitle = '대표 정보';

  private readonly is_membership_section_visible = !!(
    isMembershipSupported() && this.data.products_memberships?.length
  );

  private readonly is_use_creator_en_visible =
    !!this.data.store.formData.product_id && this.store.mode !== MODE.EDIT;

  private readonly is_product_id_visible = this.store.mode !== MODE.NEW;

  private readonly is_ness = UtilS.isNessApp();

  private inputMap = {
    [formKeyMap.is_public]: new Input_RadioGroup({
      required: true,
      name: formKeyMap.is_public,
      selected_id: String(this.store.formData._.product.is_public || false),
      items: [
        { id: 'true', display_name: '공개', value: true },
        { id: 'false', display_name: '비공개', value: false },
      ],
      title: '공개 상태',
      onChange: (view) => {
        this.data.store.actions.setField(formKeyMap.is_public, view.getSelected()?.value);
      },
      readonly: this.store.formData.is_prohibited,
      error: this.store.formData.is_prohibited
        ? '현재 판매가 일시 중단되었습니다. 마플샵 고객센터로 문의해 주세요.'
        : undefined,
      description: this.store.formData.is_prohibited
        ? undefined
        : this.is_membership_section_visible
          ? '비공개 멤버십 플랜으로 설정한 상품은 샵에서 보이지 않습니다.'
          : this.is_ness
            ? '상품 공개 여부가 "공개"여도 공개기간이 아니면 비공개와 같습니다.'
            : undefined,
    }),
    [formKeyMap.is_use_creator_en]: new Input_RadioGroup({
      required: true,
      name: formKeyMap.is_use_creator_en,
      selected_id: String(this.store.formData._.product?._?.base_product?.is_use_creator_en),
      readonly: Boolean(this.data?.is_created_sku),
      items: [
        { id: 'true', display_name: '해외 배송 가능', value: true },
        { id: 'false', display_name: '해외 배송 불가', value: false },
      ],
      title: '해외 배송 설정',
      onChange: (view) => {
        const is_intl = Boolean(view.getSelected()?.value);

        // 해외 배송 모드 전환시 sku와 spo_option을 비워줌
        this.data.store.actions.setField(
          formKeyMap.spo_items,
          this.isIntlFormData(this.store.formData, this.store.mode)
            ? this.store.formData._.product._.spo._.spo_items.map((spo_item) => {
                if (spo_item._.sku) deleteKeys(spo_item._.sku, 'width', 'height', 'length', 'weight');
                spo_item.group_names_option_names?.forEach((group_names_option_name) =>
                  deleteKeys(
                    group_names_option_name,
                    'group_name_en',
                    'group_name_jp',
                    'option_name_en',
                    'option_name_jp',
                  ),
                );
                return spo_item;
              })
            : this.store.formData._.product._.spo._.spo_items,
        );
        this.data.store.actions.setField(
          formKeyMap.spo_options,
          this.isIntlFormData(this.store.formData, this.store.mode)
            ? this.store.formData._.product._.spo._.spo_options.map((spo_option) => {
                deleteKeys(spo_option, 'name_en', 'name_jp');
                spo_option._.spo_option_values.forEach((spo_option_value) =>
                  deleteKeys(spo_option_value, 'name_en', 'name_jp'),
                );
                return spo_option;
              })
            : this.store.formData._.product._.spo._.spo_options.map((spo_option) => {
                assignKeys(spo_option, null, 'name_en', 'name_jp');
                spo_option._.spo_option_values.forEach((spo_option_value) =>
                  assignKeys(spo_option_value, null, 'name_en', 'name_jp'),
                );
                return spo_option;
              }),
        );

        if (is_intl) {
          !this.store.formData.refund_info_en &&
            this.store.actions.setField(
              formKeyMap.refund_info_en,
              MShopStudioPbProductTmplS.refundInfoNormalExEn,
            );
          !this.store.formData.refund_info_jp &&
            this.store.actions.setField(
              formKeyMap.refund_info_jp,
              MShopStudioPbProductTmplS.refundInfoNormalExJp,
            );
        }

        this.store.actions.setField(formKeyMap.is_use_creator_en, is_intl);
      },
    }),
    [formKeyMap.profit]: new Input_Price({
      required: true,
      name: formKeyMap.profit,
      value: this.store.formData._.product.profit,
      title: '소비자 가격',
      sign: MONEY_SIGN.kr,
      min: 0,
    }),
  };

  // 어드민 권한이 존재하고 비공개 상태일 때 수수료 변경 가능
  private tplFeeInput = new Input_NumberEditor({
    title: '입고 수수료',
    required: true,
    name: formKeyMap.tpl_fee,
    value: this.store.formData._?.tpl_fee?.ratio || 0,
    onFocusOut: (view) => {
      this.store.actions.setField(formKeyMap.tpl_fee, view.getNumericValue() || 0);
    },
    readonly: !this.data.is_studio_worker_admin || Boolean(this.data.is_created_sku),
    suffix: '%',
    option: {
      max: 100,
      min: 0,
      decimal: 1,
      is_commify: false,
    },
  });

  private productIdInput = new Input_TextEditor({
    required: false,
    name: formKeyMap.name,
    value: String(this.store.formData.product_id) ?? '',
    title: '상품 ID',
    option: {
      readonly: true,
    },
  });

  private hsCodeInput = new Input_NumberEditor({
    name: formKeyMap.hs_code,
    value: isEmpty(this.store.formData._.hs_code) ? null : Number(this.store.formData._.hs_code),
    title: html`
      <div class="${common_klass.title_with_tooltip}">
        <span class="${common_klass.text} ${shareFont('14_bold')}">HS CODE</span>
        ${required_html}
        ${new Tooltip({
          id: formKeyMap.pre_discount_price,
          content: html`
            <div class="${common_klass.tooltip_content_container} ${shareFont('14_medium')}">
              <div class="${common_klass.tooltip_content_divider}">
                <span>·</span>
                <span
                  >HS CODE는 국제적으로 통용되는 물품 분류 체계로 상품의 종류를 숫자로 식별하는
                  코드입니다.</span
                >
              </div>
              <div class="${common_klass.tooltip_content_divider}">
                <span>·</span>
                <span>대한민국 관세청에서 상품별 HS Code 확인해 주세요.</span>
              </div>
            </div>
          `,
          size: 'x_small',
          variant: 'studio',
        })}
      </div>
    `,
    onFocusOut: (view) => {
      this.store.actions.setField(formKeyMap.hs_code, view.getValue() || null);
    },
    option: {
      min: 0,
      is_commify: false,
      placeholder: '숫자 10자리를 입력해 주세요.',
    },
  });

  private productNameInput = this.isIntlFormData(this.store.formData, this.store.mode)
    ? new Input_TextEditor_Intl({
        kr: {
          required: true,
          name: formKeyMap.name,
          value: this.store.formData._.product.name || '',
          title: '상품명',
          title_right_klass: klass.section_text_editor_title_intl,
          onFocusout: (view) => {
            this.data.store.actions.setField(formKeyMap.name, view.getValue() || undefined);
          },
          option: {
            max_length: 100,
            placeholder: '국문 상품명을 입력해 주세요.',
          },
        },
        en: {
          required: true,
          name: formKeyMap.name_en,
          value: this.store.formData._.product.name_en || '',
          title: '',
          title_klass: klass.section_text_editor_title_intl,
          title_right_klass: klass.section_text_editor_title_intl,
          onFocusout: (view) => {
            this.data.store.actions.setField(formKeyMap.name_en, view.getValue() || undefined);
          },
          option: {
            max_length: 100,
            placeholder: '영문 상품명을 입력해 주세요.',
          },
        },
        jp: {
          required: true,
          name: formKeyMap.name_jp,
          value: this.store.formData._.product.name_jp || '',
          title: '',
          title_klass: klass.section_text_editor_title_intl,
          title_right_klass: klass.section_text_editor_title_intl,
          onFocusout: (view) => {
            this.data.store.actions.setField(formKeyMap.name_jp, view.getValue() || undefined);
          },
          option: {
            max_length: 100,
            placeholder: '일문 상품명을 입력해 주세요.',
          },
        },
      })
    : new Input_TextEditor({
        required: true,
        name: formKeyMap.name,
        value: this.store.formData._.product.name || '',
        title: '상품명',
        option: {
          max_length: 100,
          trim: true,
          placeholder: '예시) 반려동물 뜨개 케이프',
        },
        onFocusout: (view) => {
          this.data.store.actions.setField(formKeyMap.name, view.getValue() || undefined);
        },
      });

  private categoryInput = new Input_Category({
    mpsCategories: this.data.mpsCategories,
    sp_cate_root_code: this.store.formData.sp_cate_root_code,
    sp_cate_list_code: this.store.formData.sp_cate_list_code,
    sp_cate_item_code: this.store.formData.sp_cate_item_code,
    setField: (keyName, value) => {
      this.data.store.actions.setField(keyName, value);
    },
    // sku가 존재하면 카테고리 변경 불가능하도록 READ_ONLY
    status: this.data.is_created_sku ? FORM_DROPDOWN_CONSTANT.READ_ONLY : FORM_DROPDOWN_CONSTANT.ACTIVE,
  });

  private materialInput = new Input_TextEditor({
    required: true,
    name: formKeyMap.material,
    value: this.store.formData.material ?? '',
    title: '의류 소재',
    option: {
      placeholder: '의류 소재를 영문으로 입력해 주세요.',
    },
    onFocusout: (view) => {
      this.data.store.actions.setField(formKeyMap.material, view.getValue() || undefined);
    },
  });

  private profitShareInput = new Input_NumberEditor({
    name: formKeyMap.seller_profit_share,
    value: isNil(this.store.formData.seller_profit_share)
      ? 0
      : Number(((1 - (this.store.formData.seller_profit_share || 0)) * 100).toFixed(2)),
    required: true,
    title: this.is_ness ? '기업 수익 비율' : '플러스 수익 비율',
    suffix: '%',
    readonly:
      (this.store.mode !== MODE.NEW && this.data.is_public) ||
      !(this.data.is_studio_worker_admin || this.data.is_crew_admin) ||
      Boolean(this.store.formData._.product._?.has_up),
    onFocusOut: (view) => {
      const company_share_percentage =
        (isNumber(view.getNumericValue()) ? view.getNumericValue() || 0 : 100) / 100;
      this.store.actions.setField(formKeyMap.seller_profit_share, 1 - company_share_percentage);
    },
    option: {
      max: 100,
      min: 0,
      decimal: 2,
      is_commify: false,
    },
  });

  /**
   * NESS 공개기간
   */
  private publicPeriodInput = new Input_TextEditor({
    required: false,
    name: formKeyMap.product_id,
    value: isEmpty(this.store.formData._.product?._?.ness_product_metas)
      ? ''
      : pipe(
          this.store.formData._.product?._?.ness_product_metas || [],
          map((ness_products_meta) => {
            if (!ness_products_meta) return ``;
            const start_at = new Date(ness_products_meta.start_at);
            const end_at = new Date(ness_products_meta.end_at);
            return `${format(start_at, 'yyyy.MM.dd HH:mm')} ~ ${format(new Date(end_at), 'yyyy.MM.dd HH:mm')}`;
          }),
          reduce((a, b) => `${a}\n\n${b}`),
        ),
    title: '공개 기간 (변경은 개발팀에 문의)',
    description: '아래의 공개 상태 여부가 반드시 "공개"여야 정상동작 합니다.',
    option: {
      readonly: true,
      min_rows: this.store.formData._.product?._?.ness_product_metas?.length,
    },
  });

  /**
   * NESS 마케팅 가격 인풋
   */
  private marketingPriceInput = new Input_NumberEditor({
    title: html`
      <div class="${common_klass.title_with_tooltip} ${common_klass.padding}">
        <span class="${common_klass.text} ${shareFont('14_bold')}">마케팅 가격</span>
        ${new Tooltip({
          id: formKeyMap.pre_discount_price,
          content: html`
            <div class="${common_klass.tooltip_content_container} ${shareFont('14_medium')}">
              <div class="${common_klass.tooltip_content_divider}">
                <div>
                  <div>
                    마케팅 가격은 실제 가격에 영향을 주지 않는 마케팅 목적의 할인 이전 가격을 의미합니다.
                  </div>
                  <div>(소비자 가격 대비 100원 이상의 금액만 입력 가능)</div>
                </div>
              </div>
            </div>
          `,
          size: 'x_small',
          variant: 'studio',
        })}
      </div>
    `,
    name: formKeyMap.pre_discount_price,
    value: this.store.formData._.product?.pre_discount_product?.pre_discount_price
      ? PriceS.price(this.store.formData._.product?.pre_discount_product?.pre_discount_price)
      : 0,
    suffix: '원',
    option: {
      placeholder: '100원 단위로 입력해주세요.',
    },
    onFocusOut: (view) => {
      let price = view.getNumericValue() || 0;

      /* 마케팅 가격이 소비자 가격과 동일한 금액인 경우 +100 자동 보정 */
      if (price === this.store.formData._.product.profit) {
        view.setValue(PriceS.add(this.store.formData._.product.profit, 100));
        price = PriceS.add(this.store.formData._.product.profit, 100);
      }

      pipe(
        this.marketingRadioInput.getViews(),
        each((view) => {
          if (view instanceof Input_TextEditor) {
            const display = this.marketingDiscountDisplay(price, this.store.formData._.product.profit);
            view
              .setValue(display)
              .setDescription(display === '-' ? '1% 미만의 할인률은 화면에 표기되지 않습니다.' : '')
              .redraw();
          }
        }),
      );

      if (PriceS.add(this.store.formData._.product.profit, 100) > price) {
        this.store.actions.setError(formKeyMap.pre_discount_price, errorMessage.min_marketing_price);
        return;
      }

      this.store.actions.setField(formKeyMap.pre_discount_price, view.getNumericValue() || 0);
    },
  });

  private marketingDescriptionInput = new Input_TextEditor({
    title: html`<div class="${common_klass.title_with_tooltip} ${common_klass.padding}">
      <span class="${common_klass.text} ${shareFont('14_bold')}">마케팅 할인율</span>
    </div>`,
    name: formKeyMap.product_id,
    value: this.marketingDiscountDisplay(
      this.store.formData._.product?.pre_discount_product?.pre_discount_price || 0,
      this.store.formData._.product.profit,
    ),
    suffix: '%',
    option: {
      readonly: true,
    },
  }).setDescription(
    this.marketingDiscountDisplay(
      this.store.formData._.product?.pre_discount_product?.pre_discount_price || 0,
      this.store.formData._.product.profit,
    ) === '-'
      ? '1% 미만의 할인률은 화면에 표기되지 않습니다.'
      : '',
  );

  private marketingRadioInput = new Input_AddionalRadioGroup({
    name: formKeyMap.product_id,
    additional: {
      views: [this.marketingPriceInput, this.marketingDescriptionInput],
      isShow: (selected_id) => selected_id === String(true),
    },
    selected_id: String(Boolean(this.store.formData._.product?.pre_discount_product?.pre_discount_price)),
    items: [
      {
        id: 'true',
        display_name: '설정',
        value: true,
      },
      { id: 'false', display_name: '미설정', value: false },
    ],
    title: html`
      <div class="${klass.pre_discount_tooltip}">
        <span class="${common_klass.text} ${shareFont('14_bold')}">마케팅 가격</span>
        <div class="${klass.pre_discount_example}">
          <span class="${klass.pre_discount_example_title} ${shareFont('12_medium')}">예시</span>
          <div class="${klass.pre_discount_example_image}">
            <img src="${PreDiscountPriceExampleImage.ForNonPOD}" alt="" />
          </div>
        </div>
      </div>
    `,
    onChange: (show) => {
      if (show) this.store.actions.setField(formKeyMap.pre_discount_price, 0);
      else this.store.actions.setField(formKeyMap.pre_discount_price, null);
      this.marketingPriceInput.setValue(0);
    },
  });

  public override inputViews(): Array<View> {
    return pipe(
      [
        this.is_ness && this.store.mode !== MODE.NEW ? this.publicPeriodInput : null,
        this.inputMap.is_public,
        this.is_use_creator_en_visible ? this.inputMap.is_use_creator_en : null,
        this.store.mode === MODE.EDIT_TPL || this.store.mode === MODE.EDIT_TPL_INTL ? this.tplFeeInput : null,
        this.is_product_id_visible ? this.productIdInput : null,
        this.store.mode === MODE.EDIT_TPL_INTL ? this.hsCodeInput : null,
        this.productNameInput,
        this.categoryInput,
        this.isClothes() ? this.materialInput : null,
        this.inputMap.profit,
        this.is_ness ? this.marketingRadioInput : null,
        this.data.is_plus ? this.profitShareInput : null,
        this.is_membership_section_visible
          ? new MembershipSettingSection(
              {
                memberships: this.data.products_memberships ?? [],
              },
              {
                group_klass: klass.membership_group,
              },
            )
          : null,
      ],
      compact,
      toArray,
    );
  }

  override onRender() {
    this.addEventListener(EditorInputFocusoutEvent, (e) => {
      if (this.isValidInputKey(e.detail.name)) {
        const view = this.inputMap[e.detail.name];
        view && this.store.actions.setField(view.getName(), view.getValue());
      }
    });

    this.delegate(MultiSelectButtonGroupClickEvent, MultiSelectButtonGroup<string>, (e) => {
      const membership_ids = e.detail.selected.map(({ id }) => String(id));
      this.store.actions.setField(formKeyMap.membership_ids, membership_ids);
    });

    // productNameInput의 인스턴스 타입이 Input_TextEditor이면 Intl 에디터
    if (this.productNameInput instanceof Input_TextEditor) {
      subscribeStore(this.store, {
        ...this.inputMap,
        ...(this.is_product_id_visible ? { [formKeyMap.product_id]: this.productIdInput } : {}),
        ...(this.store.mode === MODE.EDIT_TPL_INTL ? { [formKeyMap.hs_code]: this.hsCodeInput } : {}),
        [formKeyMap.name]: this.productNameInput,
        [formKeyMap.sp_cate_root_code]: this.categoryInput,
        [formKeyMap.sp_cate_list_code]: this.categoryInput,
        [formKeyMap.sp_cate_item_code]: this.categoryInput,
      });
    } else {
      this.bindIntlEvent();
    }

    if (this.is_ness) this.bindNessEvent();
  }

  private bindNessEvent() {
    subscribeStore(this.store, {
      [formKeyMap.pre_discount_price]: this.marketingPriceInput,
    });
  }

  private bindIntlEvent() {
    if (this.productNameInput instanceof Input_TextEditor_Intl) {
      subscribeStore(this.store, {
        ...this.inputMap,
        ...(this.is_product_id_visible ? { [formKeyMap.product_id]: this.productIdInput } : {}),
        ...(this.store.mode === MODE.EDIT_TPL_INTL ? { [formKeyMap.hs_code]: this.hsCodeInput } : {}),
        ...(this.isClothes() ? { [formKeyMap.material]: this.materialInput } : {}),
        [formKeyMap.name]: this.productNameInput.editors.kr,
        [formKeyMap.name_en]: this.productNameInput.editors.en,
        [formKeyMap.name_jp]: this.productNameInput.editors.jp,
        [formKeyMap.sp_cate_root_code]: this.categoryInput,
        [formKeyMap.sp_cate_list_code]: this.categoryInput,
        [formKeyMap.sp_cate_item_code]: this.categoryInput,
      });
    }

    /**
     * 해외 배송 모드에서 sp_cate_list_code가 바뀌면 의류 소재 초기화
     */
    this.store.subscribeState(
      (state) => state.formData.sp_cate_list_code,
      (state) => {
        this.redraw();
        this.materialInput.setValue('').redraw();
        this.store.actions.setField(formKeyMap.material, null);
        if (state === CategoryLevel2_CODE.APPAREL && !this.subscribeStoreList.has(formKeyMap.material)) {
          this.subscribeStoreList.add(formKeyMap.material);
          subscribeStore(this.store, {
            [formKeyMap.material]: this.materialInput,
          });
        }
      },
    );
  }

  private marketingDiscountDisplay(pre_discount_price: number, profit: number) {
    return String(
      getPreDiscountRateDisplayValue(
        Math.max(0, calcDiscountedRateByPercent(pre_discount_price, profit) || 0),
      ),
    );
  }

  private isClothes() {
    return (
      this.store.mode === MODE.EDIT_TPL_INTL &&
      this.categoryInput.getCateListDropDown().getSelectedItemName() === '의류'
    );
  }

  private isValidInputKey(key: string): key is keyof typeof this.inputMap {
    return key in this.inputMap;
  }

  private isIntlFormData(
    formData: CreatorProductState<T>['formData'],
    mode: MODE,
  ): formData is CreatorProductState<typeof MODE.EDIT_TPL_INTL>['formData'] {
    return formData && mode === MODE.EDIT_TPL_INTL;
  }
}
